import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";

export type NotificationsData = components["schemas"]["NotificationsOut"];
export type NotificationItem = components["schemas"]["NotificationOut"];

/** Stored in-app notifications (ADR 0009 rule 5); live updates invalidate the query. */
export function useNotifications() {
  return useQuery({ queryKey: ["notifications"], queryFn: () => call<NotificationsData>(api.GET("/app/v1/notifications")) });
}

export function useMarkRead() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const one = useMutation({
    mutationFn: (id: string) => call(api.POST("/app/v1/notifications/{notification_id}/read", { params: { path: { notification_id: id } } })),
    onSuccess: done,
  });
  const all = useMutation({ mutationFn: () => call(api.POST("/app/v1/notifications/read-all")), onSuccess: done });
  return { one, all };
}

/** Rendered at read time in the reader's language from type + params (ADR 0017 rule 8). */
export function notificationKey(n: NotificationItem): string {
  return n.type.startsWith("notification.") ? n.type : "notification.generic";
}

export function notificationLink(n: NotificationItem): string | null {
  const id = n.params.display_id;
  if (typeof id !== "string") return null;
  if (n.entity_type === "creative_request") return `/requests/${id}`;
  if (n.entity_type === "production_job") return id.startsWith("EJ-") ? `/editor/jobs/${id}` : `/production/jobs/${id}`;
  return null;
}
